import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { EuroIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getPaymentCondition } from '../../api/payment-conditions/get-payment-condition'
import { updatePaymentCondition } from '../../api/payment-conditions/update-payment-condition'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import PaymentConditionForm from '../../components/pages/payment-condition/payment-condition-form'
import { mapPaymentConditionToInput } from '../../components/pages/payment-condition/utils'
import { Paths } from '../../lib/constants/path'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const PaymentConditionUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  const { mutateAsync } = useMutation({
    mutationFn: updatePaymentCondition,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['payment-conditions', id],
        exact: true
      })
      toast.success('Condition de paiement mise à jour avec succès')
    }
  })

  const { data: paymentCondition, isPending } = useQuery({
    queryKey: ['payment-conditions', id],
    queryFn: () => {
      if (!id || isNaN(+id)) return
      return getPaymentCondition(+id)
    }
  })

  if (isPending) return <LoadingScreen />
  if (!paymentCondition) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Mettre à jour la condition de paiement'} icon={EuroIcon} />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <PaymentConditionForm
          defaultValues={mapPaymentConditionToInput(paymentCondition)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.PAYMENT_CONDITIONS_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: paymentCondition.id, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: paymentCondition.id, ...values })
              navigate(Paths.PAYMENT_CONDITIONS_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: paymentCondition.id, ...values })
              back(Paths.PAYMENT_CONDITIONS_LIST_PAGE)
            }
          })}
        />
      </Box>
    </div>
  )
}

export default PaymentConditionUpdatePage
